import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import tw, { css } from 'twin.macro'
import { debounce } from 'lodash'

import { useListenDraftPrimaryColorQuery, useSetDraftPrimaryColorMutation } from 'modules/PageBuilder/pageBuilderApi'

import SalesHeroPreview from './SalesHeroPreview'
import SalesHighlightsPreview from './SalesHighlightsPreview'
import SalesBannerPreview from './SalesBannerPreview'
import SalesAboutPreview from './SalesAboutPreview'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { ColorInput } from 'common/components/Color/ColorInput'
import { inputBase } from 'common/components/Input/Input'
import { buttonBase, buttonSizes, buttonVariants } from 'common/components/Button/Button'
import { DisplayModeSwitch } from '../DisplayModeSwitch/DisplayModeSwitch'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'

import logo from 'assets/images/TrybeLogoWhite.svg'
import appStore from 'assets/images/download-app-store.svg'
import googlePlay from 'assets/images/download-google-play.svg'
import FaqPreview from './FaqPreview'
import TestimonialPreview from './TestimonialPreview'
import { inputIds, inputNames } from '../../constants/salesInputConstants'

export const HEADER_HEIGHT = 64

export default function SalesPagePreview({ coachOrgId, page, handlePublishPage, isPublishing }) {
  const { productId } = useParams()

  const [displayMode, setDisplayMode] = useState('desktop')
  const isMob = displayMode === 'mobile'

  const { data: primaryColorData } = useListenDraftPrimaryColorQuery({ coachOrgId, productId })
  const primaryColor = primaryColorData || '#000000'

  const [setPrimaryColor] = useSetDraftPrimaryColorMutation()
  const debouncedSetColor = debounce((color) => setPrimaryColor({ coachOrgId, color, productId }), 1000)

  const { moveFocusOnKeyPress } = useFormRefsControl()
  useEventListener('keydown', (e) => {
    if (
      e.code === 'Enter' &&
      (e.target.name === inputNames.heroBtnText ||
        e.target.name === inputNames.aboutBtnText ||
        e.target.name === inputNames.hlSectionTitle ||
        (e.target.name === inputNames.highlight && e.target.id === inputIds.highlightTitleId) ||
        e.target.name === inputNames.aboutTitle)
    ) {
      e.preventDefault()
    } else {
      moveFocusOnKeyPress(e)
    }
  })

  return (
    <>
      <div className='flex justify-between items-end mb-5 w-full'>
        <div className='w-1/3'>
          <ColorInput
            label='Primary Color'
            watchColor={primaryColor}
            onChange={(color) => debouncedSetColor(color)}
            inputCSS={[inputBase, tw`h-8 p-2 w-20 rounded ml-2`]}
            containerClasses='!my-0'
          />
        </div>
        <div className='flex justify-center w-1/3'>
          <DisplayModeSwitch displayMode={displayMode} setDisplayMode={setDisplayMode} />
        </div>
        <div className='w-1/3'>
          <Dialog>
            <DialogTrigger css={[buttonBase, buttonSizes.lg, buttonVariants.primary, tw`ml-auto`]} aria-label='Publish'>
              Publish page
            </DialogTrigger>
            <DialogContent>
              <CreateDialogBanner
                text={`This will publish the changes of Sales Page`}
                handleCreate={handlePublishPage}
                createBtnText='Publish'
                loading={isPublishing}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div css={[tw`flex flex-col w-full border-offWhite border`, isMob && tw`max-w-sm`]}>
        <div className='flex items-center px-2 py-4 bg-black' style={{ height: HEADER_HEIGHT }}>
          <img src={logo} alt='logo' className='w-8 h-8' />
          <span css={[tw`text-3xl text-white uppercase ml-2 leading-none tracking-widest`, isMob && tw`text-xl`]}>
            TRYBE
          </span>
        </div>
        <SalesHeroPreview coachOrgId={coachOrgId} section={page?.heroSection || {}} isMob={isMob} />
        <SalesHighlightsPreview coachOrgId={coachOrgId} section={page?.programSummary || {}} isMob={isMob} />
        <SalesBannerPreview coachOrgId={coachOrgId} section={page?.banner || {}} isMob={isMob} />
        <TestimonialPreview coachOrgId={coachOrgId} testimonials={page?.testimonials} isMob={isMob} />
        <SalesAboutPreview coachOrgId={coachOrgId} section={page?.aboutMe || {}} isMob={isMob} />
        <FaqPreview coachOrgId={coachOrgId} faqs={page?.faqs} isMob={isMob} />
        <div
          css={[
            isMob ? tw`text-lg` : tw`text-2xl`,
            tw`flex flex-col items-center justify-center text-white font-bold py-5 px-4`,
            css`
              background-color: ${primaryColor};
            `,
          ]}
        >
          <p className='mb-5 text-center'>Already signed up? Download the app here</p>
          <div className='flex items-center'>
            <img src={appStore} alt='app store download' />
            <img src={googlePlay} alt='google play download' className='ml-2.5' />
          </div>
        </div>
      </div>
    </>
  )
}
